import { onUnmounted } from 'vue'
import {
  ParsedGlbCache,
  AssemblyManifestCache,
  AssemblyModelCache,
  PartAssetCache,
  GlbResourceCache,
  type GlbResourceCacheOptions,
} from '@puppycad/cad-core'
import {
  createImportedBrepRuntimeCache,
  type ImportedBrepRuntimeCache,
} from '@/services/loaders/imported-model-brep-runtime-cache'
import { logger } from '@/utils'

/**
 * 缓存实例集合。
 * 将所有导入模型相关的缓存集中管理，方便统一注入与销毁。
 */
export interface ImportedModelCacheGroup {
  /** 已解析的 GLTF 对象缓存 */
  parsedGlbCache: ParsedGlbCache
  /** 装配体清单缓存 */
  assemblyManifestCache: AssemblyManifestCache
  /** 装配体模型资产缓存 */
  assemblyModelCache: AssemblyModelCache
  /** 零件资产缓存 */
  partAssetCache: PartAssetCache
  /** GLB 原始资源缓存（含 LRU 淘汰） */
  glbResourceCache: GlbResourceCache
  /** 导入 BRep 运行时缓存，负责 sidecar 与 MeshData 去重 */
  importedBrepRuntimeCache: ImportedBrepRuntimeCache
}

export interface UseImportedModelCacheOptions {
  /** GlbResourceCache 最大条目数（默认 6） */
  maxGlbResourceEntries?: number
}

/**
 * 导入模型缓存管理 hook。
 *
 * 职责：
 * 1. 创建并持有所有导入模型缓存实例；
 * 2. 提供 dispose 方法手动清理所有缓存（释放 GPU 资源）；
 * 3. 在组件 unmount 时自动调用 dispose。
 *
 * 典型用法：
 * ```ts
 * const { caches, dispose } = useImportedModelCache()
 * // 将 caches 传给 useProjectLoader.loadGlbFiles() 等函数
 * ```
 */
export function useImportedModelCache(options: UseImportedModelCacheOptions = {}) {
  const parsedGlbCache = new ParsedGlbCache()
  const assemblyManifestCache = new AssemblyManifestCache()
  const assemblyModelCache = new AssemblyModelCache()
  const partAssetCache = new PartAssetCache()
  const importedBrepRuntimeCache = createImportedBrepRuntimeCache()

  const glbResourceCacheOptions: GlbResourceCacheOptions = {}
  if (options.maxGlbResourceEntries != null) {
    glbResourceCacheOptions.maxEntries = options.maxGlbResourceEntries
  }
  const glbResourceCache = new GlbResourceCache(glbResourceCacheOptions)

  /** 缓存实例集合，方便作为整体注入 */
  const caches: ImportedModelCacheGroup = {
    parsedGlbCache,
    assemblyManifestCache,
    assemblyModelCache,
    partAssetCache,
    glbResourceCache,
    importedBrepRuntimeCache,
  }

  /** 是否已经销毁 */
  let disposed = false

  /**
   * 获取所有缓存的统计信息，用于调试。
   */
  function stats() {
    return {
      parsedGlb: parsedGlbCache.stats(),
      assemblyManifest: assemblyManifestCache.stats(),
      assemblyModel: assemblyModelCache.stats(),
      partAsset: partAssetCache.stats(),
      glbResource: { size: glbResourceCache.size() },
      importedBrepRuntime: importedBrepRuntimeCache.stats(),
    }
  }

  /**
   * 清理所有缓存实例。
   * 释放 Three.js 几何体、材质、纹理等 GPU 资源。
   */
  function dispose(): void {
    if (disposed) {
      return
    }
    disposed = true

    const snapshot = stats()
    logger.info('[imported-model-cache] 开始清理缓存', snapshot)

    // PartAssetCache / AssemblyModelCache / GlbResourceCache 在 clear() 时都会释放
    // 自己持有的 GPU 资源（包括 assembly sceneTemplate）。
    partAssetCache.clear()
    assemblyModelCache.clear()
    glbResourceCache.clear()

    // 以下缓存不持有 GPU 资源，直接清空即可
    assemblyManifestCache.clear()
    parsedGlbCache.clear()
    importedBrepRuntimeCache.clear()

    logger.info('[imported-model-cache] 缓存清理完成')
  }

  onUnmounted(() => {
    dispose()
  })

  return {
    caches,
    parsedGlbCache,
    assemblyManifestCache,
    assemblyModelCache,
    partAssetCache,
    glbResourceCache,
    importedBrepRuntimeCache,
    stats,
    dispose,
  }
}
